import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'; import { Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './common/ScrollToTop';
import LoadingFallback from './components/LoadingFallback';
import SkipLink from './components/SkipLink';
import A11yAnnouncer from './components/A11yAnnouncer';
import useRouteFocus from './hooks/useRouteFocus';

// Eagerly loaded components (critical for initial render)
import Home from './pages/Home';
import Services from './pages/Services';

// Lazy loaded components (non-critical routes)
const About = lazy(() => import('./pages/About.jsx'));
const Contact = lazy(() => import('./pages/Contact.jsx'));
const Book = lazy(() => import('./pages/Book.jsx'));
const Onboarding = lazy(() => import('./pages/Onboarding.jsx'));
const Articles = lazy(() => import('./pages/Articles.jsx'));
const ArticleDetail = lazy(() => import('./pages/ArticleDetail.jsx'));
const Process = lazy(() => import('./pages/Process.jsx'));
const Disclaimer = lazy(() => import('./pages/Disclaimer.jsx'));
const Success = lazy(() => import('./pages/Success.jsx'));
const Privacy = lazy(() => import('./pages/Privacy.jsx'));
const Terms = lazy(() => import('./pages/Terms.jsx'));
const Cookies = lazy(() => import('./pages/Cookies.jsx'));
const CategoryArticles = lazy(() => import('./pages/CategoryArticles.jsx'));
const Resources = lazy(() => import('./pages/Resources.jsx'));
const FAQ = lazy(() => import('./pages/FAQ.jsx'));
const Support = lazy(() => import('./pages/Support.jsx'));
const NewsletterPopup = lazy(() => import('./components/NewsletterPopup'));
const CookieConsent = lazy(() => import('./components/CookieConsent'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Shop = lazy(() => import('./pages/Shop'));
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/dashboard/index.jsx'));
const DashboardSettings = lazy(() => import('./pages/dashboard/Settings'));
const DashboardContent = lazy(() => import('./pages/dashboard/Content'));
const DashboardScripts = lazy(() => import('./pages/dashboard/Scripts'));
const AccountPortal = lazy(() => import('./pages/account/index.jsx'));
const AuthCallback = lazy(() => import('./pages/AuthCallback.jsx'));
const Inbox = lazy(() => import('./pages/Inbox.jsx'));

// Service Pages (lazy loaded)
const StrategyAdvisory = lazy(() => import('./pages/mentorship/StrategyAdvisory.jsx'));
const AIAutomation = lazy(() => import('./pages/mentorship/AIAutomation.jsx'));
const VideoMedia = lazy(() => import('./pages/mentorship/VideoMedia.jsx'));
const CreativeConsulting = lazy(() => import('./pages/mentorship/CreativeConsulting.jsx'));

const DigitalProducts = lazy(() => import('./pages/mentorship/DigitalProducts.jsx'));
const ProductDetail = lazy(() => import('./pages/mentorship/ProductDetail.jsx'));
const Checkout = lazy(() => import('./pages/Checkout.jsx'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess.jsx'));

const CryptoEducation = lazy(() => import('./pages/mentorship/CryptoEducation.jsx'));
const CryptoConsulting = lazy(() => import('./pages/mentorship/CryptoConsulting.jsx'));
const InvestmentsVentures = lazy(() => import('./pages/mentorship/InvestmentsVentures.jsx'));
const PropertyManagement = lazy(() => import('./pages/mentorship/PropertyManagement.jsx'));
const CommunityEvents = lazy(() => import('./pages/mentorship/CommunityEvents.jsx'));
const SpecialProjects = lazy(() => import('./pages/mentorship/SpecialProjects.jsx'));

import { CartProvider } from './context/CartContext';
import CartDrawer from './components/CartDrawer';
import './App.css';

import { SiteContentProvider, useSiteContent } from './context/SiteContentContext';

// RouteFocus wrapper component to use the hook inside Router context
const RouteFocusHandler = () => {
  useRouteFocus();
  return null;
};

// Global Script Injector - Injects scripts from dashboard into the site
const ScriptInjector = () => {
  const { settings, loading } = useSiteContent();
  const [injected, setInjected] = useState(false);

  useEffect(() => {
    if (loading || injected) return;

    // Inject Header Scripts
    if (settings.header_scripts) {
      try {
        // Create a temporary container to parse the HTML
        const container = document.createElement('div');
        container.innerHTML = settings.header_scripts;
        // Move all elements to head
        Array.from(container.children).forEach(node => {
          const clonedNode = node.cloneNode(true);
          clonedNode.setAttribute('data-injected', 'dashboard');
          document.head.appendChild(clonedNode);
        });
        console.log('[Dashboard] Header scripts injected');
      } catch (err) {
        console.error('Error injecting header scripts:', err);
      }
    }

    // Inject Footer Scripts
    if (settings.footer_scripts) {
      try {
        const container = document.createElement('div');
        container.innerHTML = settings.footer_scripts;
        // Move all elements to end of body
        Array.from(container.children).forEach(node => {
          const clonedNode = node.cloneNode(true);
          clonedNode.setAttribute('data-injected', 'dashboard');
          document.body.appendChild(clonedNode);
        });
        console.log('[Dashboard] Footer scripts injected');
      } catch (err) {
        console.error('Error injecting footer scripts:', err);
      }
    }

    setInjected(true);
  }, [settings, loading, injected]);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <SiteContentProvider>
            <AuthProvider>
              <CartProvider>
                <Router>
                  <ScrollToTop />
                  <RouteFocusHandler />
                  <ScriptInjector />
                  <A11yAnnouncer />

                  <Routes>
                    {/* Standalone routes - no Header/Footer */}
                    <Route path="/admin/login" element={
                      <Suspense fallback={<LoadingFallback />}>
                        <Login />
                      </Suspense>
                    } />
                    {/* Legacy Dashboard Redirect - now consolidated into /account */}
                    <Route path="/dashboard/*" element={<Navigate to="/account" replace />} />

                    {/* User Account Portal - separate from admin */}
                    <Route path="/account/*" element={
                      <Suspense fallback={<LoadingFallback />}>
                        <AccountPortal />
                      </Suspense>
                    } />

                    {/* Auth callback for email verification */}
                    <Route path="/auth/callback" element={
                      <Suspense fallback={<LoadingFallback />}>
                        <AuthCallback />
                      </Suspense>
                    } />

                    {/* Inbox - standalone messaging page */}
                    <Route path="/inbox" element={
                      <Suspense fallback={<LoadingFallback />}>
                        <Inbox />
                      </Suspense>
                    } />

                    {/* Main site routes - with Header/Footer */}
                    <Route path="/*" element={
                      <>
                        <SkipLink />
                        <CartDrawer />
                        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col font-sans transition-colors duration-300">
                          <Header />
                          <main
                            id="main-content"
                            className="flex-grow outline-none"
                            tabIndex="-1"
                          >
                            <ErrorBoundary>
                              <Suspense fallback={<LoadingFallback />}>
                                <Routes>
                                  {/* Eagerly loaded routes */}
                                  <Route path="/" element={<Home />} />
                                  <Route path="/mentorship" element={<Services />} />
                                  {/* Redirect old /services to /mentorship */}
                                  <Route path="/services" element={<Navigate to="/mentorship" replace />} />

                                  {/* Lazy loaded service routes */}
                                  <Route path="/mentorship/strategy-advisory" element={<StrategyAdvisory />} />
                                  <Route path="/mentorship/ai-automation" element={<AIAutomation />} />
                                  <Route path="/mentorship/video-media" element={<VideoMedia />} />
                                  <Route path="/mentorship/creative-consulting" element={<CreativeConsulting />} />
                                  <Route path="/mentorship/digital-products" element={<Navigate to="/shop" replace />} />
                                  <Route path="/mentorship/digital-products/:slug" element={<Navigate to="/shop" replace />} />
                                  <Route path="/shop/digital-products/:slug" element={<ProductDetail />} />
                                  <Route path="/checkout" element={<Checkout />} />
                                  <Route path="/checkout/success" element={<CheckoutSuccess />} />

                                  <Route path="/mentorship/crypto-education" element={<CryptoEducation />} />
                                  <Route path="/mentorship/crypto-consulting" element={<CryptoConsulting />} />
                                  <Route path="/mentorship/investments-ventures" element={<InvestmentsVentures />} />
                                  <Route path="/mentorship/property-management" element={<PropertyManagement />} />
                                  <Route path="/mentorship/community-events" element={<CommunityEvents />} />
                                  <Route path="/mentorship/special-projects" element={<SpecialProjects />} />

                                  {/* Redirects for old /services sub-routes */}
                                  <Route path="/services/*" element={<Navigate to="/mentorship" replace />} />

                                  {/* Lazy loaded article routes */}
                                  <Route path="/articles" element={<Articles />} />
                                  <Route path="/articles/category/:slug" element={<CategoryArticles />} />
                                  <Route path="/articles/read/:slug" element={<ArticleDetail />} />

                                  {/* Lazy loaded other routes */}
                                  <Route path="/shop" element={<Shop />} />
                                  <Route path="/about" element={<About />} />
                                  <Route path="/contact" element={<Contact />} />
                                  <Route path="/book" element={<Book />} />
                                  <Route path="/onboarding" element={<Onboarding />} />
                                  <Route path="/process" element={<Process />} />
                                  <Route path="/success" element={<Success />} />
                                  {/* Redirect old /work to /success */}
                                  <Route path="/work" element={<Navigate to="/success" replace />} />
                                  <Route path="/resources" element={<Resources />} />
                                  <Route path="/faq" element={<FAQ />} />
                                  <Route path="/support" element={<Support />} />
                                  <Route path="/disclaimer" element={<Disclaimer />} />
                                  <Route path="/privacy" element={<Privacy />} />
                                  <Route path="/terms" element={<Terms />} />
                                  <Route path="/cookies" element={<Cookies />} />
                                  <Route path="*" element={<NotFound />} />
                                </Routes>
                              </Suspense>
                            </ErrorBoundary>
                          </main>
                          <Footer />
                          <Suspense fallback={null}>
                            <NewsletterPopup />
                            <CookieConsent />
                          </Suspense>
                        </div>
                      </>
                    } />
                  </Routes>
                </Router>
              </CartProvider>
            </AuthProvider>
          </SiteContentProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;